import type { ChangeEvent } from "react";
import { useRef } from "react";

import { t } from "@/i18n-js/instance";

import type { CompassPromptMenuHandle } from "../CompassPromptMenu";
import { CompassPromptMenu } from "../CompassPromptMenu";
import { LoaderPrompt } from "../LoaderPrompt";
import { AiIcon } from "../Prompt/AiIcon";
import { Input } from "../Prompt/Input";
import {
  createPromptKeydownHandler,
  useNativeInputKeydown,
} from "../useNativeInputKeydown";
import { useDraftPrompt } from "./useDraftPrompt";

const COMPLETION_VALUES = [
  "suggest_ideas",
  "outline",
  "paragraph",
  "conclusion",
] as const;

const COMPLETIONS_I18N_ROOT =
  "tiptap.extensions.ai_copilot.prompt_options.completions";

export const CompassDraftPrompt = () => {
  const {
    addPromptPrefix,
    cleanupNode,
    handleSubmit,
    inputRef,
    isLoading,
    node,
    setUserPrompt,
    userPrompt,
  } = useDraftPrompt();

  const menuRef = useRef<CompassPromptMenuHandle>(null);

  const options = COMPLETION_VALUES.map(value => ({
    id: value,
    label: t([COMPLETIONS_I18N_ROOT, value, "label"]),
  }));

  const selectPromptOption = (index: number) => {
    const value = COMPLETION_VALUES[index];
    addPromptPrefix(value, t([COMPLETIONS_I18N_ROOT, value, "prompt_prefix"]));
  };

  useNativeInputKeydown(
    inputRef,
    createPromptKeydownHandler({
      getMenu: () => menuRef.current,
      getUserPrompt: () => userPrompt,
      onEscape: cleanupNode,
      onSubmit: handleSubmit,
    }),
    isLoading,
  );

  if (isLoading) {
    return <LoaderPrompt />;
  }

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="border-primary bg-primary flex items-start gap-3 rounded-lg border px-4 py-2.5">
          <AiIcon />
          <Input
            ref={inputRef}
            id={`input-${node.attrs?.id}`}
            placeholder={node.attrs?.placeholder}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              setUserPrompt(event.target.value)
            }
            value={userPrompt}
          />
        </div>
      </form>
      {!userPrompt && (
        <CompassPromptMenu
          ref={menuRef}
          label={t("tiptap.extensions.ai_copilot.prompt_options.title")}
          options={options}
          onSelect={selectPromptOption}
        />
      )}
    </>
  );
};
